import { useApiQuery } from '@/hooks/useApiQuery';
import { apiGet, operationsClient } from '@/lib/api';
import { settingsEndpoints } from '@/lib/api/endpoints';
import { settingsKeys } from '@/lib/queryKeys';
import type { UserListResponse } from '@/types/settings';

const USERS_PAGE_SIZE = 100;

/**
 * The users table searches and sorts client-side, so its query must include
 * every page. Fetching only page one made users beyond the first 100
 * impossible to find (including newly invited users in long-lived tenants).
 */
export async function fetchAllUsers(): Promise<UserListResponse> {
  const firstPage = await apiGet<UserListResponse>(
    operationsClient,
    settingsEndpoints.users(),
    { params: { page: 1, limit: USERS_PAGE_SIZE } },
  );
  const remainingPages = await Promise.all(
    Array.from({ length: Math.max(0, firstPage.totalPages - 1) }, (_, index) =>
      apiGet<UserListResponse>(operationsClient, settingsEndpoints.users(), {
        params: { page: index + 2, limit: USERS_PAGE_SIZE },
      }),
    ),
  );
  const data = [firstPage, ...remainingPages].flatMap((page) => page.data);

  return { ...firstPage, data, hasMore: false };
}

export function useUsers() {
  return useApiQuery<UserListResponse>({
    queryKey: settingsKeys.users(),
    queryFn: () =>
      apiGet<UserListResponse>(operationsClient, settingsEndpoints.users(), {
        params: { limit: USERS_PAGE_SIZE },
      }),
    showErrorToast: false,
  });
}

/** Full roster for Settings views that search, sort, and filter client-side. */
export function useAllUsers() {
  return useApiQuery<UserListResponse>({
    queryKey: settingsKeys.allUsers(),
    queryFn: fetchAllUsers,
    showErrorToast: false,
  });
}
